/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes, ember/no-incorrect-calls-with-inline-anonymous-functions */
/* eslint-disable ember/no-runloop */
import { computed, set, setProperties } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import {
  cancel,
  debounce,
  later,
  type Timer as EmberRunTimer,
} from '@ember/runloop';
import Service, { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { isTesting, macroCondition } from '@embroider/macros';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import { tKey } from 'ember-intl';
import type { Hotel, Ota } from 'frontend-utils/models';
import {
  OTA_IDS,
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import registerObserver from 'frontend-utils/utils/register-observer';
import type { Liveupdate, Parity, Period } from 'lumen/models';
import type { LiveupdateCompareParams } from 'lumen/models/liveupdate';
import type { PeriodParity, PeriodRate } from 'lumen/models/period';
import type { ParityRequestParams } from 'lumen/routes/hotel/parity/list';
import {
  APIGATEWAY,
  type CompareType,
  COMPARETYPES,
  type MealTypeValues,
  type MembershipType,
  MEMBERSHIPTYPES,
  RATE_PARAMS,
  type RateParam,
  RATETYPES,
} from 'lumen/utils/constants/general-constants';
import {
  type CountryCodeWithDefault,
  DEFAULT_POS,
} from 'lumen/utils/constants/pi-constant-values';
import { PA_REF_DAYS_AGO } from 'lumen/utils/constants/pricing-assistant-constants';
import { overlaps } from 'lumen/utils/dates/moment-helpers';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import {
  mapCompareParamsToAPIParams,
  mapRateParamToAPIParams,
} from 'lumen/utils/rates-request';

export const LIVEUPDATE_TYPE_RATES = 'rates';
const LIVEUPDATE_TYPE_RATES_COMPARE = 'rates_compare';
export const LIVEUPDATE_TYPE_PARITY = 'parity';
const LIVEUPDATE_TYPE_PARITY_POS = 'parity_pos';
export const LIVEUPDATE_TYPE_PARITY_INSIGHT = 'parity_analytics';

export type ParityType =
  | typeof LIVEUPDATE_TYPE_PARITY
  | typeof LIVEUPDATE_TYPE_PARITY_POS
  | typeof LIVEUPDATE_TYPE_PARITY_INSIGHT;

// Interval between fetching periods
const POLLING_INTERVAL_IN_MILLISECONDS = 10000;

// Interval between fetching liveupdates
const LIVE_UPDATE_POLLING_INTERVAL = 60 * 1000;
const LIVE_UPDATE_ACTIVE_POLLING_INTERVAL = 10 * 1000;

export interface RawLiveupdate {
  id: string;
  ota: string | Ota;
  status: number;
  type: string;
}

interface LiveupdateLimits {
  limit_concurrent_subscription_high_prio_dayshop: boolean;
  limit_concurrent_subscription_high_prio_monthshop: boolean;
  limit_concurrent_subscription_high_prio_monthshop_brand: boolean;
  limit_concurrent_user_high_prio_monthshop: boolean;
  limit_concurrent_user_high_prio_monthshop_brand: boolean;
  limit_concurrent_user_mid_prio_shops: boolean;
  limit_daily_subscription_high_prio_monthshop: boolean;
  limit_subscription_mid_prio_days: boolean;
}

interface LiveupdateSelections {
  compsetId: number;
  currentHotelId: string | null;
  isParityAnalytics?: boolean;
  los: number;
  maxPersons: number;
  mealtype: MealTypeValues | null;
  membershipType: MembershipType;
  meta?: string;
  month: string | null;
  platform: Platform;
  rateChanges?: string | null;
  rateParam: RateParam;
  roomtype: string | null;
  selectedOTA: string | null;
}

interface ParityLiveupdateParams {
  base_membershiptype: MembershipType;
  filter_otas?: string[];
  los: number;
  membershiptype: MembershipType;
  persons: number;
  platform: Platform;
  pos?: CountryCodeWithDefault;
}

interface DateRange {
  end: Dayjs;
  start: Dayjs;
}

export interface LiveupdateObject {
  customRange: boolean;
  fromDate: Dayjs;
  hotel: Hotel;
  labels: string;
  ota: Ota;
  params: {
    compare_params?: Record<string, unknown>;
    compset_ids: number[];
    los: number;
    mealtype: number | null;
    membershiptype: number;
    persons: number;
    platform: number;
    roomtype: string | null;
  };
  priority: number;
  sendEmail: boolean;
  toDate: Dayjs;
  type: string;
}

export default class LiveupdateService extends Service {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked parityTabOpen = false;
  @tracked pricingAssistantTabOpen = false;

  compareType: CompareType = '';
  compareValue = '';
  compsetId = 1;
  // Id of the hotel
  currentHotelId: string | null = null;
  isParityAnalytics?: boolean | undefined = false;
  // debounce for liveupdate polling
  liveUpdateDebounce = null;
  liveupdateLimits: LiveupdateLimits | null = null;
  // All liveupdates for this hotel
  liveupdates: Liveupdate[] = [];
  // timer for running liveupdate polling
  liveupdateTimer: EmberRunTimer | null = null;
  los = 1;
  maxPersons = 0;
  mealtype: MealTypeValues | null = null;
  membershipType: MembershipType = 0;
  meta?: string | undefined = 'nested';
  month: string | null = null;
  parityOverviewTabOpen = false;
  parityPOSTabOpen = false;
  platform: Platform = PLATFORMS.PLATFORM_UNSPECIFIED;
  pos: CountryCodeWithDefault = DEFAULT_POS;

  rateChanges?: string | null = null;
  rateParam: RateParam = RATE_PARAMS.LOWEST;
  ratesTabOpen = false;
  roomtype: string | null = null;
  // Id of the selected OTA
  selectedOTA: string | null = null;
  showMemberRate = false;

  /**
   * Schedules a poll event for fetching periods
   * @param  { function } event    : function to schedule
   * @param  { int } interval      : the interval in ms
   * @return { int }              : id of scheduled function, so we can cancel if needed
   */
  private schedulePollEventTask = task(
    { drop: true },
    async (event: (() => void) | undefined): Promise<void> => {
      await timeout(POLLING_INTERVAL_IN_MILLISECONDS);
      event?.apply(this);
      // Recursive scheduling, this needs to be cancelled in order to stop (see stopPolling)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.schedulePollEventTask.perform(event);
    },
  );
  /**
   * Starts the polling for periods
   * @param  { function } pollEvent : function to execute when polling
   * @param  { int } interval       : interval of the polling
   */
  private startPollingTask = task(
    { drop: true },
    async (pollEvent: (() => void) | undefined): Promise<void> => {
      // Debounce cause the selections observer caused the liveupdates to be started multiple times.
      await timeout(250);

      if (macroCondition(!isTesting())) {
        pollEvent?.apply(this);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.schedulePollEventTask.perform(pollEvent);
      }
    },
  );

  @dependentKeyCompat
  get activeLiveupdatesForThisSelection(): Liveupdate[] | undefined {
    return this.liveupdatesForThisSelection?.completeLiveupdates;
  }

  /**
   * @private
   */
  @computed('month', 'currentHotelId')
  get dateRange(): {
    end: Dayjs;
    start: Dayjs;
  } {
    const dateString = `${this.month}-01`;

    const startOfMonth = this.hotelAndMarketService
      .toLocalTime(dateString)
      .startOf('month');
    const endOfMonth = startOfMonth.endOf('month');

    const today = this.hotelAndMarketService.getLocalTime().startOf('day');

    return {
      start: startOfMonth.isBefore(today, 'day') ? today : startOfMonth,
      end: endOfMonth,
    };
  }

  /**
   * @private
   */
  @computed('liveupdates.[]')
  get hasPostsWithoutResponse(): boolean {
    return this.liveupdates.some((liveupdate) => !liveupdate.id);
  }

  @computed('liveupdateLimits.limit_concurrent_subscription_high_prio_dayshop')
  get isDayLiveupdateLimitReached(): boolean {
    // When limits are still loading, give user the option to start a liveupdate
    if (this.liveupdateLimits === null) {
      return false;
    }

    if (
      this.liveupdateLimits?.limit_concurrent_subscription_high_prio_dayshop
    ) {
      return true;
    }

    return false;
  }

  @computed(
    'liveupdateLimits.{limit_concurrent_subscription_high_prio_monthshop,limit_concurrent_subscription_high_prio_monthshop_brand,limit_concurrent_user_high_prio_monthshop,limit_concurrent_user_high_prio_monthshop_brand,limit_daily_subscription_high_prio_monthshop}',
    'parityTabOpen',
    'selectedOTA',
  )
  get isMonthLiveupdateLimitReached(): boolean {
    // When limits are still loading, give user the option to start a liveupdate
    if (this.liveupdateLimits === null) {
      return false;
    }

    if (
      this.liveupdateLimits?.limit_concurrent_subscription_high_prio_monthshop
    ) {
      return true;
    }
    if (this.liveupdateLimits?.limit_concurrent_user_high_prio_monthshop) {
      return true;
    }

    // Branddotcom limits
    if (this.selectedOTA === 'branddotcom') {
      if (
        this.liveupdateLimits
          ?.limit_concurrent_subscription_high_prio_monthshop_brand
      ) {
        return true;
      }
      if (
        this.liveupdateLimits?.limit_concurrent_user_high_prio_monthshop_brand
      ) {
        return true;
      }
    }

    // Hotel-level PA restricted parity view limit
    if (
      this.liveupdateLimits?.limit_daily_subscription_high_prio_monthshop &&
      this.parityTabOpen
    ) {
      return true;
    }

    return false;
  }

  @computed('activeLiveupdatesForThisSelection.length')
  get isPolling(): boolean {
    // @ts-expect-error: Incorrect type
    return this.activeLiveupdatesForThisSelection?.length > 0;
  }

  /**
   * @private
   */
  @computed(
    'compareType',
    'compareValue',
    'currentHotelId',
    'dateRange.{end,start}',
    'isParityAnalytics',
    'liveupdates.[]',
    'los',
    'maxPersons',
    'month',
    'parityOverviewTabOpen',
    'parityPOSTabOpen',
    'parityTabOpen',
    'platform',
    'pos',
    'pricingAssistantTabOpen',
    'rateParam',
    'ratesTabOpen',
    'selectedOTA',
  )
  get liveupdatesForThisSelection(): {
    completeLiveupdates: Liveupdate[];
    partialLiveupdates: Liveupdate[];
  } {
    const completeLiveupdates: Liveupdate[] = [];
    const partialLiveupdates: Liveupdate[] = [];

    this.liveupdates.forEach((liveupdate) => {
      if (
        this.currentHotelId === null ||
        liveupdate.hotel?.id !== this.currentHotelId
      ) {
        return false; // break from forEach
      }

      const { params } = liveupdate;

      if (this.ratesTabOpen) {
        const { compareType, compareValue, month, selectedOTA, dateRange } =
          this;

        const defaultConditions = this.ratesDefaultConditions(
          liveupdate,
          selectedOTA,
          month,
          dateRange,
        );

        if (!defaultConditions) {
          return false;
        }

        const isCompareWithExtraLiveupdate = this.isCompareWithExtraLiveupdate(
          compareType,
          compareValue,
        );

        // Check if the current selection is being shopped in the liveupdate object (default RATES + RATES_COMPARE case)
        const shoppingSelectionInLiveUpdate =
          this.isSelectionShoppedInParams(params);
        // Check if the current compare (los, persons or platform) is being shopped in the liveupdate object
        const shoppingCompareInLiveUpdate = this.isCompareShoppedInParams(
          params,
          compareType,
          compareValue,
        );

        if (liveupdate.type === LIVEUPDATE_TYPE_RATES) {
          // Comparing los, person or platform
          if (isCompareWithExtraLiveupdate) {
            // Check if either the current selection or the current compare is being shopped in the liveupdate object
            if (shoppingSelectionInLiveUpdate || shoppingCompareInLiveUpdate) {
              partialLiveupdates.push(liveupdate);
            }
          }
          // Not comparing or comparison with only 1 liveupdate (= liveupdate type matches selection)
          // Check if current selection is shopped in the liveupdate object
          else if (shoppingSelectionInLiveUpdate) {
            completeLiveupdates.push(liveupdate);
          }
        } else if (liveupdate.type === LIVEUPDATE_TYPE_RATES_COMPARE) {
          const compareParamsObj =
            params?.compare_params as LiveupdateCompareParams;

          // Check if the current selection is being shopped in the compare_params property of the liveupdate object
          const shoppingSelectionInCompareParams =
            this.isSelectionShoppedInParams(compareParamsObj);
          // Check if the current compare (los, persons or platform) is being shopped in the compare_params of the liveupdate object (default RATES_COMPARE case)
          const shoppingCompareInCompareParams = this.isCompareShoppedInParams(
            compareParamsObj,
            compareType,
            compareValue,
          );

          // Comparing los, person or platform (= liveupdate type matches selection)
          if (isCompareWithExtraLiveupdate) {
            // Check if both the current selection is being shopped in the liveupdate object and the current compare is being shopped in the compare_params of the liveupdate object, also check the other way around
            if (
              (shoppingSelectionInLiveUpdate &&
                shoppingCompareInCompareParams) ||
              (shoppingSelectionInCompareParams && shoppingCompareInLiveUpdate)
            ) {
              completeLiveupdates.push(liveupdate);
            }
            // Check if the current selection and/or the current compare is being shopped in the liveupdate object and/or in the compare_params of the liveupdate object
            else if (
              shoppingSelectionInLiveUpdate ||
              shoppingCompareInCompareParams ||
              shoppingSelectionInCompareParams ||
              shoppingCompareInLiveUpdate
            ) {
              partialLiveupdates.push(liveupdate);
            }
          }
          // Not comparing or comparison with only 1 liveupdate
          // Check if current selection is shopped in the liveupdate object or in the compare_params of the liveupdate object
          else if (
            shoppingSelectionInLiveUpdate ||
            shoppingSelectionInCompareParams
          ) {
            completeLiveupdates.push(liveupdate);
          }
        }
      }

      if (this.pricingAssistantTabOpen) {
        const { month, selectedOTA, dateRange } = this;

        // We check same default conditions as for rates
        const defaultConditions = this.ratesDefaultConditions(
          liveupdate,
          selectedOTA,
          month,
          dateRange,
        );

        if (!defaultConditions) {
          return false;
        }

        // Check if the pricing mode filters/selections are being shopped in the liveupdate object
        const shoppingSelectionInLiveUpdate =
          this.isSelectionShoppedInParams(params);

        if (
          liveupdate.type === LIVEUPDATE_TYPE_RATES &&
          shoppingSelectionInLiveUpdate
        ) {
          completeLiveupdates.push(liveupdate);
        }
      }

      if (this.parityTabOpen) {
        const isParityAnalyticsLiveupdate =
          liveupdate.type === LIVEUPDATE_TYPE_PARITY_INSIGHT &&
          this.isParityAnalytics;
        const isParityLiveupdate =
          liveupdate.type === LIVEUPDATE_TYPE_PARITY && !this.isParityAnalytics;
        const isLosSame = params.los === this.los;
        const isPosSame = (params.pos ?? DEFAULT_POS) === this.pos;
        const isPlatformSame = params.platform === this.platform;
        const isPersonsCorrect =
          params.persons.toString() === this.maxPersons.toString() ||
          params.persons.toString() === '0';
        const isMonthNotEmpty = this.month !== null;
        const daterangeOverlaps = overlaps(
          this.dateRange?.start,
          this.dateRange?.end,
          liveupdate.fromDate,
          liveupdate.toDate,
        );
        const isCorrectPriority = liveupdate.priority === 0;

        if (
          (isParityAnalyticsLiveupdate || isParityLiveupdate) &&
          isLosSame &&
          isPosSame &&
          isPlatformSame &&
          isPersonsCorrect &&
          isMonthNotEmpty &&
          daterangeOverlaps &&
          isCorrectPriority
        ) {
          completeLiveupdates.push(liveupdate);
        }
      }

      if (this.parityOverviewTabOpen) {
        if (
          liveupdate.type === LIVEUPDATE_TYPE_PARITY &&
          params.los === this.los &&
          (params.persons.toString() === this.maxPersons.toString() ||
            params.persons.toString() === '0')
        ) {
          completeLiveupdates.push(liveupdate);
        }
      }

      if (this.parityPOSTabOpen) {
        if (
          liveupdate.type === LIVEUPDATE_TYPE_PARITY_POS &&
          liveupdate.priority === 0
        ) {
          completeLiveupdates.push(liveupdate);
        }
      }

      return false;
    });
    return {
      completeLiveupdates,
      partialLiveupdates,
    };
  }

  get partialLiveupdatesForThisSelection(): Liveupdate[] | undefined {
    return this.liveupdatesForThisSelection?.partialLiveupdates;
  }

  init(...args: Parameters<Service['init']>): void {
    super.init(...args);

    registerObserver(
      this,
      ['hasPostsWithoutResponse'],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.arePostRequestsWaitingForResponse,
    );
    registerObserver(
      this,
      [
        'activeLiveupdatesForThisSelection.[]',
        'hasPostsWithoutResponse',
        'ratesTabOpen',
        'parityTabOpen',
        'parityOverviewTabOpen',
        'parityPOSTabOpen',
      ],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.activeLiveupdateObserver,
    );
    // eslint-disable-next-line @typescript-eslint/unbound-method
    registerObserver(this, ['currentHotelId'], this.onHotelChange);

    this.setupService();
  }

  /**
   * TODO: this function is bad, we should rewrite this
   * We stop the periods request every time a liveupdate request comes in and then immediately start it again
   * This is because this observer listens to activeLiveupdatesForThisSelection
   * The concurrency seems to fix it somewhat but we should really try to work out a better solution here
   */
  private async activeLiveupdateObserver(): Promise<void> {
    // If there are liveupdates for the current selections
    // Trigger one initial period poll asap
    let pollEvent;
    if (this.ratesTabOpen) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      pollEvent = this.onPollEventRates;
    }
    if (this.pricingAssistantTabOpen) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      pollEvent = this.onPollEventPricing;
    }
    if (this.parityTabOpen) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      pollEvent = this.onPollEventParity;
    }
    if (this.parityOverviewTabOpen) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      pollEvent = this.onPollEventParity;
    }
    if (this.parityPOSTabOpen) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      pollEvent = this.onPollEventParityPOS;
    }

    if (this.activeLiveupdatesForThisSelection?.length) {
      // Check if we can start polling for the active liveupdate for the current selection (aka: see if we already received the POST response)
      if (!this.hasPostsWithoutResponse) {
        await this.stopPolling();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.startPollingTask.perform(pollEvent);
      }
    } else if (
      this.schedulePollEventTask.isRunning ||
      this.startPollingTask.isRunning
    ) {
      if (pollEvent) {
        // One last request to get the updated rates
        pollEvent.apply(this);
      }
      // Selections changed, and/or no liveupdates for current selections found
      await this.stopPolling();
    }
  }

  // Check if the current compare is being shopped in the passed params (params or params.compare_params)
  private arePostRequestsWaitingForResponse(): void {
    if (this.hasPostsWithoutResponse) {
      // If there are posts waiting for responses, we have to make sure that the liveupdatePolling isn't active (avoid Ember store problems)
      // => If the poll response comes before the POST response of the liveupdate creation, then Ember store will end up with double rows...
      this.clearLiveupdatePolling();
    } else {
      // Path is clear. Free to poll.
      this.startLiveupdatePolling();
    }
  }

  /**
   * Check if the general liveupdate params are correct
   * @liveupdateObject  { object } liveupdateObject : An object with all params for the liveupdate
   * @return { string }          The message to be tranlated for the rror, or an empty string if all is correct
   */
  private checkLiveUpdate(liveupdateObject: {
    fromDate: Dayjs;
    hotel: Hotel;
    toDate: Dayjs;
  }): string {
    let message = '';
    if (!liveupdateObject.fromDate.isValid()) {
      message = 'error.liveupdate.general';
    }
    if (!liveupdateObject.toDate.isValid()) {
      message = 'error.liveupdate.general';
    }
    if (
      liveupdateObject.fromDate.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      )
    ) {
      message = 'error.liveupdate.to_date_from_date';
    }
    if (liveupdateObject.toDate.isBefore(liveupdateObject.fromDate, 'day')) {
      message = 'error.liveupdate.to_date_from_date';
    }
    if (!liveupdateObject.hotel) {
      message = 'error.liveupdate.general';
    }
    return message;
  }

  /**
   * Check if the parity liveupdate params are correct
   * @liveupdateObject  { object } liveupdateObject : An object with all params for the liveupdate
   * @return { string }          The message to be tranlated for the error, or an empty string if all is correct
   */
  private checkLiveUpdateParity(liveupdateObject: {
    fromDate: Dayjs;
    hotel: Hotel;
    toDate: Dayjs;
  }): string {
    return this.checkLiveUpdate(liveupdateObject);
  }

  /**
   * Check if the rates liveupdate params are correct
   * @liveupdateObject  { object } liveupdateObject : An object with all params for the liveupdate
   * @return { string }          The message to be tranlated for the error, or an empty string if all is correct
   */
  private checkLiveUpdateRates(liveupdateObject: {
    fromDate: Dayjs;
    hotel: Hotel;
    ota: Ota;
    toDate: Dayjs;
  }): string {
    let message = this.checkLiveUpdate(liveupdateObject);
    if (!liveupdateObject.ota) {
      message = 'error.liveupdate.general';
    }
    return message;
  }

  private clearLiveupdatePolling(): void {
    cancel(this.liveupdateTimer as EmberRunTimer);
    set(this, 'liveupdateTimer', null);
  }

  private createLiveupdate(
    liveupdateObject: Partial<RawLiveupdate>,
  ): Liveupdate {
    const liveupdate = this.store.createRecord<Liveupdate>(
      'liveupdate',
      // @ts-expect-error: Argument of type 'Partial<RawLiveupdate>' is not assignable to parameter of type 'Partial<FilteredKeys<Liveupdate>>'.
      liveupdateObject,
    );

    set(this, 'liveupdates', [...this.liveupdates, liveupdate]);

    return liveupdate;
  }

  private debounceFetchLiveupdates(): void {
    // Debouncing since the limits are requested upon the answer of a liveupdate start, and these can come in directly after each other
    // Only debounce by a small time because we need up to date limits
    const debounceTimeMs = 100;
    if (this.hasPostsWithoutResponse) {
      // Hacky: debouncing the own function is sort of using a timeout to call yourself after x amount of time.
      // The difference here is that if there are multiple calls they will get bundeled and so only one will actually continue.
      // Basically it will wait untill all posts have a response before fetching liveupdates.
      // This is required since otherwise duplicates of the liveupdate might end up in the Ember datastore. (see explanation at arePostRequestsWaitingForResponse)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      debounce(this, this.debounceFetchLiveupdates, debounceTimeMs);
    } else {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      debounce(this, this.onLiveupdatePollEvent, debounceTimeMs);
    }
  }

  // Generic code that is shared between the isParityUpdating & isParityPosUpdating checks
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private displayGenericLiveupdateError(
    message: string,
    isShopReduction = false,
  ) {
    if (isShopReduction) {
      this.errorTracking.addBreadcrumb({
        message: 'Shop reduction liveupdate error',
        data: { error: message },
      });
    }
    // Display the error message in case of an error
    return this.alerting.showErrorAlert({
      title: this.intl.t('error.liveupdate.failed'),
      text: message,
    });
  }

  /**
   * Fetches the periods
   * @param  { object } request : parameters for the request
   */
  private fetchPeriods(request: object): void {
    try {
      this.store
        // @ts-expect-error: Incorrect type
        .query<Period>('period', request)
        .then(undefined, (adaptererror) => {
          if (adaptererror) {
            this.errorTracking.captureException(adaptererror);

            // If the adaptererror is undefined it means a redirect happended and the request was cancelled
            if (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              adaptererror?.errors &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              adaptererror?.errors[0] &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (adaptererror.errors[0].status === '403' ||
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                adaptererror.errors[0].status === '401')
            ) {
              window.location.href = '/';
            }
          }
        });
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  private handleLiveupdateCreateError(
    error: unknown,
    liveupdate: Liveupdate,
  ): void {
    // If the liveupdate was created and an error occured after, delete the record
    if (liveupdate !== null) {
      this.removeLiveupdate(liveupdate);
    }

    this.errorTracking.captureException(error);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      title: this.intl.t('error.liveupdate.failed'),
      text: this.intl.t('error.liveupdate.general'),
    });
  }

  /**
   * Determines the message to show upon failure. Also resets the liveupdate service state to before the liveupdate was started
   * @param  { object } liveupdate : represents the started liveupdate
   * @param  { object } response     : API response
   * @param  { boolean } isShopReduction : is it a shop reduction?
   */
  private handleLiveupdateErrorResponse(
    liveupdate: Liveupdate,
    response: { errors?: { detail: string }[] },
    isShopReduction = false,
  ): void {
    this.errorTracking.addBreadcrumb({ data: liveupdate });
    this.errorTracking.addBreadcrumb({ data: response });

    this.removeLiveupdate(liveupdate);

    if (
      response &&
      response.errors &&
      response.errors[0] &&
      response.errors[0].detail
    ) {
      const key = response.errors[0].detail;

      let errorDetail = this.intl.t(key);
      const errorDetailExists = this.intl.exists(key);
      // Fallback for translations
      if (!errorDetailExists) {
        errorDetail = this.intl.t('error.liveupdate.general');
      }

      if (isShopReduction && errorDetailExists) {
        return;
      }

      const errorModal = this.displayGenericLiveupdateError(
        errorDetail.toString(),
        isShopReduction,
      );
      // Do a hard refresh for disconnected OTAs
      if (
        response.errors[0].detail === tKey('error.liveupdate.ota_disconnected')
      ) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        errorModal.then(() => {
          window.location.href = '/';
        });
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.displayGenericLiveupdateError(
        this.intl.t('error.liveupdate.general').toString(),
        isShopReduction,
      );
    }
  }

  private handleLiveupdatePayload(
    liveupdatePayload: { liveupdates: [RawLiveupdate, ...RawLiveupdate[]] },
    custom = false,
  ): void {
    // The save method returns the new payload from backend, we manually inject it into the store
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('liveupdate', liveupdatePayload);
    // We get the injected record from store and add it back to the active liveupdates for this selection
    const newLiveupdate = this.store.peekRecord<Liveupdate>(
      'liveupdate',
      liveupdatePayload.liveupdates[0].id,
    );

    if (this.access.isStaffUser) {
      console.log(
        `%cLiveupdate debug link: ${window.location.origin}/admin/shopping/appliveupdate/${liveupdatePayload.liveupdates[0].id}/change/`,
        'color:#0093D6;font-size:17px;',
      );
    }

    // Update the liveupdates with the new data to make spinners active
    if (newLiveupdate) {
      set(this, 'liveupdates', [...this.liveupdates, newLiveupdate]);
    }

    if (custom) {
      this.toast.success(this.intl.t('rates.liveupdates.success_report_long'), {
        title: this.intl.t('rates.liveupdates.success_title'),
        showIcon: true,
      });
    } else {
      const { status, type, ota } = liveupdatePayload.liveupdates[0];
      switch (status) {
        case 100:
          // Liveupdate will succeed
          this.toast.success(this.intl.t('rates.liveupdates.success_long'), {
            title: this.intl.t('rates.liveupdates.success_title'),
            showIcon: true,
          });
          break;
        case 99:
          // Liveupdate will partially succeed
          if (
            type === LIVEUPDATE_TYPE_RATES ||
            type === LIVEUPDATE_TYPE_RATES_COMPARE
          ) {
            const messageKey =
              ota === 'branddotcom'
                ? tKey('rates.liveupdates.partial_feedback_brand')
                : tKey('rates.liveupdates.partial_feedback');
            this.toast.success(this.intl.t(messageKey), {
              title: this.intl.t('rates.liveupdates.success_title'),
              showIcon: true,
            });
          } else if (
            type === LIVEUPDATE_TYPE_PARITY ||
            type === LIVEUPDATE_TYPE_PARITY_POS ||
            type === LIVEUPDATE_TYPE_PARITY_INSIGHT
          ) {
            this.toast.success(
              this.intl.t('parity.liveupdates.partial_feedback'),
              {
                title: this.intl.t('rates.liveupdates.success_title'),
                showIcon: true,
              },
            );
          }
          break;
        case 104:
          // Liveupdate will mostly fail
          if (
            type === LIVEUPDATE_TYPE_RATES ||
            type === LIVEUPDATE_TYPE_RATES_COMPARE
          ) {
            this.toast.alert(this.intl.t('rates.liveupdates.full_feedback'), {
              title: this.intl.t('rates.liveupdates.success_title'),
              showIcon: true,
            });
          } else if (
            type === LIVEUPDATE_TYPE_PARITY ||
            type === LIVEUPDATE_TYPE_PARITY_POS ||
            type === LIVEUPDATE_TYPE_PARITY_INSIGHT
          ) {
            this.toast.alert(this.intl.t('parity.liveupdates.full_feedback'), {
              title: this.intl.t('rates.liveupdates.success_title'),
              showIcon: true,
            });
          }
          break;
        default:
          break;
      }
    }
  }

  private isCompareShoppedInParams(
    params: LiveupdateCompareParams,
    compareType: CompareType,
    compareValue: string,
  ): boolean {
    const isThirdPartyShoppedInParams =
      params.rate_type === RATETYPES.THIRD_PARTY;
    const isThirdPartyActiveInCompare =
      compareValue === RATE_PARAMS.THIRD_PARTY;
    // When only Lowest/Best Flex is being shopped, we still need to perform an additional liveshop for 3rd Party rates. Vice versa we don't.
    const isShoppingRateTypeCompareInParams =
      compareType === COMPARETYPES.RATE_PARAM &&
      (!isThirdPartyActiveInCompare || isThirdPartyShoppedInParams);

    const isShoppingLosCompareInParams =
      compareType === COMPARETYPES.LOS &&
      params.los?.toString() === compareValue.toString();
    const isShoppingPersonsCompareInParams =
      compareType === COMPARETYPES.PERSONS &&
      (params.persons?.toString() === compareValue.toString() ||
        params.persons?.toString() === '0');
    const isShoppingPlatformCompareInParams =
      compareType === COMPARETYPES.PLATFORM &&
      params.platform?.toString() === compareValue.toString();
    // A membership compare is shopped if the IDs match or if it is "vs. Member" (shopped together with Public)
    const isShoppingMembershipTypeInCompareInParams =
      compareType === COMPARETYPES.MEMBERSHIPTYPE &&
      params.membershiptype?.toString() === compareValue.toString();

    return (
      isShoppingRateTypeCompareInParams ||
      isShoppingLosCompareInParams ||
      isShoppingPersonsCompareInParams ||
      isShoppingPlatformCompareInParams ||
      isShoppingMembershipTypeInCompareInParams
    );
  }

  private isCompareWithExtraLiveupdate(
    compareType: CompareType,
    compareValue: string,
  ): boolean {
    const isLosCompare = compareType === COMPARETYPES.LOS;
    const isPersonsCompare = compareType === COMPARETYPES.PERSONS;
    const isPlatformCompare = compareType === COMPARETYPES.PLATFORM;
    const isThirdPartyCompare =
      compareType === COMPARETYPES.RATE_PARAM &&
      (this.rateParam === RATE_PARAMS.THIRD_PARTY ||
        compareValue === RATE_PARAMS.THIRD_PARTY);

    // An extra liveupdate is required for "Public vs XXX" (but not for "Public vs. Member")
    const isMembershipCompareWithExtraLiveupdate =
      compareType === COMPARETYPES.MEMBERSHIPTYPE &&
      this.membershipType === MEMBERSHIPTYPES.PUBLIC &&
      compareValue !== `${MEMBERSHIPTYPES.MEMBER_ONLY}`;

    return (
      isLosCompare ||
      isPlatformCompare ||
      isThirdPartyCompare ||
      isPersonsCompare ||
      isMembershipCompareWithExtraLiveupdate
    );
  }

  /**
   * Checks if the parity tab is currently running a liveupdate for the current range
   * @param  { dayjs }  fromDate    : startdate of the liveupdate
   * @param  { dayjs }  currentDate : date that we want to check
   * @param  { array }   parities    : array of parities
   * @return { boolean }             : are the parities updating?
   */
  isDefaultParityUpdating(
    fromDate: Dayjs,
    currentDate: Dayjs,
    parities: PeriodParity[],
  ): boolean {
    const activeLiveupdates = this.activeLiveupdatesForThisSelection;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const checkForUpdate = function (
      updateStartTime: Dayjs,
      collection: PeriodParity[],
    ) {
      return collection.some((parityEntry) =>
        dayjs.utc(parityEntry.extract_datetime).isBefore(updateStartTime),
      );
    };

    return this.isUpdating(
      // @ts-expect-error: Incorrect type
      activeLiveupdates,
      fromDate,
      currentDate,
      parities,
      checkForUpdate,
    );
  }

  /**
   * Checks if the parity POS tab is currently running a liveupdate for the current range
   * @param  { dayjs }  fromDate    : startdate of the liveupdate
   * @param  { dayjs }  currentDate : date that we want to check
   * @param  { array }   parities    : array of parities
   * @param  { string }  pos         : pos country
   * @return { boolean }             : are the parities updating?
   */
  isPOSParityUpdating(
    fromDate: Dayjs,
    currentDate: Dayjs,
    parities: PeriodParity[],
    pos: string,
  ): boolean {
    // @ts-expect-error: Incorrect type
    const activeLiveupdates = this.activeLiveupdatesForThisSelection.filter(
      (activeLiveupdate) => activeLiveupdate.params?.pos === pos,
    );
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const checkForUpdate = function (
      updateStartTime: Dayjs,
      collection: PeriodParity[],
    ) {
      return collection.some((parityEntry) =>
        dayjs.utc(parityEntry.extract_datetime).isBefore(updateStartTime),
      );
    };

    return this.isUpdating(
      activeLiveupdates,
      fromDate,
      currentDate,
      parities,
      checkForUpdate,
    );
  }

  /**
   * Checks if the rates tab is currently running a liveupdate for the current range
   * @param  { dayjs }  fromDate    : startdate of the liveupdate
   * @param  { dayjs }  currentDate : date that we want to check
   * @param  { object }  rates       : object with the rates, keys are hotelinfo ids
   * @return { boolean }              : are the rates updating?
   */
  isRatesUpdating(
    fromDate: Dayjs,
    currentDate: Dayjs | undefined,
    rates: Record<string, [PeriodRate, ...PeriodRate[]]> | undefined,
  ): boolean {
    if (!rates || !currentDate) {
      return false;
    }
    const activeLiveupdates = this.activeLiveupdatesForThisSelection;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const checkForUpdate = function (
      updateStartTime: Dayjs | undefined,
      collection: Record<string, [PeriodRate, ...PeriodRate[]]>,
    ) {
      let isUpdating = false;
      Object.values(collection).forEach((value) => {
        // Loop over all rates & get the extract datetimes to compare these with the liveupdate starttime. This allows us to see if the rates have been updated

        const extractDatetime = dayjs.utc(value[0].extract_datetime);

        // While waiting on API response the isUpdating will temporarily be undefined, as this is returned by the API
        // Show spinners in that case
        // Spin until we consider the rate to be fresh
        if (
          updateStartTime === undefined ||
          extractDatetime.isBefore(updateStartTime)
        ) {
          isUpdating = true;
        }
      });

      return isUpdating;
    };

    return this.isUpdating(
      // @ts-expect-error: Incorrect type
      activeLiveupdates,
      fromDate,
      currentDate,
      rates,
      checkForUpdate,
    );
  }

  // Check if the current filters are being shopped in the passed params (params or params.compare_params)
  private isSelectionShoppedInParams(params: LiveupdateCompareParams): boolean {
    const isThirdPartyShoppedInParams =
      params.rate_type === RATETYPES.THIRD_PARTY;
    const isThirdPartyActive = this.rateParam === RATE_PARAMS.THIRD_PARTY;
    // When only Lowest/Best Flex is being shopped, we still need to perform an additional liveshop for 3rd Party rates. Vice versa we don't.
    const isShoppingRateTypeInParams =
      !isThirdPartyActive || isThirdPartyShoppedInParams;

    const isShoppingLosInParams = params.los === this.los;
    const isShoppingPersonsInParams =
      params.persons?.toString() === this.maxPersons.toString() ||
      params.persons?.toString() === '0';
    const isShoppingPlatformInParams =
      params.platform?.toString() === this.platform.toString();
    // Member is shopped together with Public (and Public is always shopped)
    const isShoppingMembershipTypeInParams =
      params.membershiptype?.toString() === this.membershipType.toString() ||
      this.membershipType === MEMBERSHIPTYPES.PUBLIC ||
      this.membershipType === MEMBERSHIPTYPES.MEMBER_ONLY;

    return (
      params &&
      isShoppingRateTypeInParams &&
      isShoppingLosInParams &&
      isShoppingPersonsInParams &&
      isShoppingPlatformInParams &&
      isShoppingMembershipTypeInParams
    );
  }

  /**
   * Checks if the parity overview is currently running a liveupdate for the current range
   * @param  { dayjs }  fromDate    : startdate of the liveupdate
   * @param  { dayjs }  currentDate : date that we want to check
   * @param  { array }   parities    : array of parities
   * @return { boolean }             : are the parities updating?
   */
  isSingleDefaultParityUpdating(
    fromDate: Dayjs,
    currentDate: Dayjs,
    parity: Parity,
  ): boolean {
    const activeLiveupdates = this.activeLiveupdatesForThisSelection;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const checkForUpdate = function (
      updateStartTime: Dayjs,
      collection: Parity,
    ) {
      return dayjs.utc(collection.extractDatetime).isBefore(updateStartTime);
    };

    return this.isUpdating(
      // @ts-expect-error: Incorrect type
      activeLiveupdates,
      fromDate,
      currentDate,
      parity,
      checkForUpdate,
    );
  }

  /**
   * @param { array } activeLiveupdates : all active liveupdates
   * @param { dayjs } fromDate         : startdate of liveupdate
   * @param { dayjs } currentDate      : current date we want to check
   * @param { array|object} collection  : rates/parities or single rate/parity
   * @param { function} checkForUpdate  : function to call to check if it's updating
   * @return { boolean }                : are the rates/parities updating?
   */
  // eslint-disable-next-line max-params
  private isUpdating<
    Collection extends PeriodParity[] | Record<string, PeriodRate[]> | Parity,
  >(
    activeLiveupdates: Liveupdate[],
    fromDate: Dayjs,
    currentDate: Dayjs,
    collection: Collection,
    checkForUpdate: (updateStartTime: Dayjs, c: Collection) => boolean,
  ): boolean {
    if (
      activeLiveupdates.length &&
      collection &&
      fromDate &&
      currentDate &&
      fromDate.isSame(currentDate, 'month')
    ) {
      let isUpdating = false;

      activeLiveupdates.forEach((activeLiveupdate) => {
        if (
          currentDate.isSameOrAfter(activeLiveupdate.fromDate, 'day') &&
          currentDate.isSameOrBefore(activeLiveupdate.toDate, 'day')
        ) {
          const updateStartTime = activeLiveupdate.startTimestamp;

          isUpdating = checkForUpdate(updateStartTime, collection);
        }
      });

      return isUpdating;
    }

    return false;
  }

  // This can cause strange behaviour because of the scheduling
  private onHotelChange(): void {
    this.setupService();
  }

  private onLiveupdatePollEvent(): void {
    let liveupdates: Liveupdate[] = [];
    const { store } = this;

    // If user is inactive for longer than 15 minutes, don't perform the request
    if (
      new Date().getTime() - this.hotelAndMarketService.lastUpdateTime <=
      900000
    ) {
      try {
        store
          .query<Liveupdate>('liveupdate', { hotel_id: this.currentHotelId })
          .then(
            (content) => {
              const metaLiveupdateLimits =
                content.meta as unknown as LiveupdateLimits & {
                  payload: unknown;
                };
              delete metaLiveupdateLimits?.payload;
              set(
                this,
                'liveupdateLimits',
                metaLiveupdateLimits as LiveupdateLimits,
              );

              liveupdates = [...liveupdates, ...content];
              set(this, 'liveupdates', liveupdates);
            },
            (adaptererror) => {
              if (adaptererror) {
                // If the adaptererror is undefined it means a redirect happended and the request was cancelled
                this.errorTracking.captureException(adaptererror);

                if (
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  adaptererror?.errors &&
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  adaptererror?.errors[0] &&
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  (adaptererror.errors[0].status === '403' ||
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    adaptererror.errors[0].status === '401')
                ) {
                  window.location.href = '/';
                }
              }
            },
          );
      } catch (error) {
        this.errorTracking.captureException(error, {
          log: `There was an issue with the liveupdates for ${this.currentHotelId}`,
        });
      }
    }
  }

  // Keep in sync with routes/hotel/parity/list.ts and /insight.ts requests
  private onPollEventParity(): void {
    const requestParams: ParityRequestParams = {
      namespace: APIGATEWAY,
      typeKey: this.isParityAnalytics ? 'parities/analytics' : 'parity', // Make sure type to poll is PA when liveupdate is PA
      hotel_id: this.currentHotelId!,
      los: this.los,
      pos: this.pos,
      persons: this.maxPersons,
      roomtype: this.roomtype!,
      mealtype: this.mealtype!,
      membershiptype: this.membershipType,
      from_date_range_start: this.dateRange?.start.format('YYYY-MM-DD'),
      from_date_range_end: this.dateRange?.end.format('YYYY-MM-DD'),
      meta: 'nested',
      platform: this.platform,
      base_membershiptype: this.showMemberRate
        ? MEMBERSHIPTYPES.MEMBER_ONLY
        : MEMBERSHIPTYPES.PUBLIC,
    };

    mapRateParamToAPIParams(this.rateParam, requestParams);

    this.fetchPeriods(requestParams);
  }

  // Keep in sync with routes/hotel/parity/pos.ts requests
  private onPollEventParityPOS(): void {
    const today = this.hotelAndMarketService.getLocalTime();
    const dateRangeStart = today.format('YYYY-MM-DD');
    const dateRangeEnd = today.add(29, 'day').format('YYYY-MM-DD');

    const request = {
      namespace: APIGATEWAY,
      typeKey: 'parities/pos',
      hotel_id: this.currentHotelId,
      from_date_range_start: dateRangeStart,
      from_date_range_end: dateRangeEnd,
      los: this.hotelAndMarketService.hotel?.defaultLosParity?.[0],
      persons: this.maxPersons,
      membershiptype: 0,
      meta: this.meta,
      platform: this.platform,
    };

    mapRateParamToAPIParams(this.rateParam, request);

    this.fetchPeriods(request);
  }

  private onPollEventPricing(): void {
    const compareDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(PA_REF_DAYS_AGO, 'day')
      .format('YYYY-MM-DD');

    const request = {
      namespace: APIGATEWAY,
      typeKey: 'pricing-assistant/rates', // Make sure type to poll is PA when liveupdate is PA
      hotel_id: this.currentHotelId,
      from_date_range_start: this.dateRange?.start.format('YYYY-MM-DD'),
      from_date_range_end: this.dateRange?.end.format('YYYY-MM-DD'),
      compare_extract_date: compareDate,
    };

    mapRateParamToAPIParams(this.rateParam, request);

    this.fetchPeriods(request);
  }

  private onPollEventRates(): void {
    const request: {
      allow_labels?: string[];
      compset_ids: number;
      from_date_range_end: string | null;
      from_date_range_start: string | null;
      hotel_id: string | null;
      los: number;
      mealtype: number | null;
      membershiptype: number;
      meta?: string;
      namespace: string;
      ota: string | null;
      persons: number;
      platform: number;
      roomtype: string | null;
      typeKey: string;
    } = {
      namespace: APIGATEWAY,
      typeKey: 'rates',
      ota: this.selectedOTA,
      hotel_id: this.currentHotelId,
      los: this.los,
      mealtype: this.mealtype,
      persons: this.maxPersons,
      roomtype: this.roomtype,
      from_date_range_start: this.dateRange?.start.format('YYYY-MM-DD'),
      from_date_range_end: this.dateRange?.end.format('YYYY-MM-DD'),
      membershiptype: this.membershipType,
      compset_ids: this.compsetId,
      platform: this.platform,
      meta: this.meta,
    };

    mapRateParamToAPIParams(this.rateParam, request);
    mapCompareParamsToAPIParams(this, request);

    if (this.platform > PLATFORMS.PLATFORM_DESKTOP) {
      request['allow_labels'] = ['no_label', 'mobile_only'];
    }

    this.fetchPeriods(request);
  }

  // Default conditions for rates liveupdate selection
  private ratesDefaultConditions(
    liveupdate: Liveupdate,
    selectedOTA: string | null,
    month: string | null,
    dateRange: DateRange,
  ): boolean {
    // For ALL_CHANNELS, we should check any OTA matching default conditions
    const checkOtaId = this.ratesTabOpen
      ? selectedOTA === OTA_IDS.ALL_CHANNELS ||
        liveupdate.ota?.id === selectedOTA
      : liveupdate.ota?.id === selectedOTA;

    const defaultConditions =
      selectedOTA !== null &&
      month !== null &&
      liveupdate.sendEmail === false &&
      checkOtaId &&
      overlaps(
        dateRange?.start,
        dateRange?.end,
        liveupdate.fromDate,
        liveupdate.toDate,
      ) &&
      liveupdate.priority === 0 &&
      !liveupdate.isCustom;

    return defaultConditions;
  }

  private removeLiveupdate(liveupdate: Liveupdate): void {
    set(
      this,
      'liveupdates',
      this.liveupdates.filter((update) => update !== liveupdate),
    );
    // @ts-expect-error: The 'this' context of type 'Liveupdate' is not assignable to method's 'this' of type 'MinimalLegacyRecord'.
    liveupdate.deleteRecord();
    this.store.unloadRecord(liveupdate);
  }

  resetPropertiesToDefault(): void {
    setProperties(this, {
      currentHotelId: null,
      selectedOTA: null,
      los: 1,
      pos: DEFAULT_POS,
      month: null,
      mealtype: null,
      maxPersons: 0,
      roomtype: null,
      rateParam: RATE_PARAMS.LOWEST,
      membershipType: 0,
      platform: PLATFORMS.PLATFORM_UNSPECIFIED,
      rateChanges: '',
      compsetId: 1,
      meta: 'nested',
      isParityAnalytics: false,
      showMemberRate: false,
    });
  }

  private scheduleLiveupdatePollEvent(
    event: () => void,
    interval?: number,
  ): EmberRunTimer | null {
    if (macroCondition(isTesting())) {
      return null;
    }

    const defaultPollingInterval = this.isPolling
      ? LIVE_UPDATE_ACTIVE_POLLING_INTERVAL
      : LIVE_UPDATE_POLLING_INTERVAL;

    const eventInterval = interval || defaultPollingInterval;

    return later(
      this,
      () => {
        event.apply(this);
        set(this, 'liveupdateTimer', this.scheduleLiveupdatePollEvent(event));
      },
      eventInterval,
    );
  }

  private setupService(): void {
    if (this.currentHotelId !== null) {
      // Clear current state
      set(this, 'liveupdates', []);
      set(this, 'liveupdateLimits', null);

      // Trigger the first liveupdate call directly
      this.onLiveupdatePollEvent();

      // start polling for liveupdates
      this.startLiveupdatePolling();
    }
  }

  startLiveParityShop(params: {
    dayShop?: string | null;
    filterOtas?: string[];
    parityType?: ParityType;
  }): void {
    // Get the default range
    let start = this.dateRange?.start;
    let end = this.dateRange?.end;

    if (params.dayShop) {
      const dayShopLocalTime = this.hotelAndMarketService.toLocalTime(
        params.dayShop,
      );
      start = dayShopLocalTime;
      end = dayShopLocalTime;
    }

    const { store } = this;
    const hotel = store.peekRecord<Hotel>(
      'hotel',
      this.currentHotelId as string,
    ) as Hotel;

    const requestParams: ParityLiveupdateParams = {
      los: this.los,
      membershiptype: this.membershipType,
      persons: this.maxPersons,
      platform: this.platform,
      base_membershiptype: this.showMemberRate
        ? MEMBERSHIPTYPES.MEMBER_ONLY
        : MEMBERSHIPTYPES.PUBLIC,
    };

    if (this.pos !== DEFAULT_POS) {
      requestParams['pos'] = this.pos;
    }

    if (params.filterOtas) {
      requestParams['filter_otas'] = params.filterOtas;
    }

    const liveupdateObject = {
      hotel,
      type: params.parityType ?? LIVEUPDATE_TYPE_PARITY,
      fromDate: start,
      toDate: end,
      // Hardcode this to make sure the liveupdates pass the checks in selectionObserver before we have received the POST response
      priority: 0,
      params: requestParams,
      labels: '',
    };

    mapRateParamToAPIParams(this.rateParam, liveupdateObject.params);

    // Check if the liveupdate params are correct
    const message = this.checkLiveUpdateParity(liveupdateObject);
    this.startLiveupdate(liveupdateObject, message);
  }

  /**
   * Starts a liveupdate for rates
   * @param  { boolean } custom   : is it a custom range liveupdate
   * @param  { string }  exStart  : start of custom liveupdate
   * @param  { string }  exEnd    : end of custom liveupdate
   * @param  { boolean }  dayShop : is it a dayshop?
   * @param  { boolean }  isShopReduction : is it a shop reduction?
   * @param  { string }  liveshopOta : OTA id to be used for the liveupdate
   */
  // eslint-disable-next-line max-params
  startLiveRateShop(
    custom = false,
    exStart: string | null = null,
    exEnd: string | null = null,
    dayShop: string | null = null,
    isShopReduction = false,
    liveshopOta: string | null = null,
  ): void {
    // Set default liveupdate range
    let start = this.dateRange?.start;
    let end = this.dateRange?.end;
    let email = false;
    // Reset to custom range if needed
    if (custom) {
      start = this.hotelAndMarketService.toLocalTime(exStart);
      end = this.hotelAndMarketService.toLocalTime(exEnd);
      email = true;
    }
    // Reset to dayshop range if needed
    if (dayShop) {
      const dayShopLocalTime = this.hotelAndMarketService.toLocalTime(dayShop);
      start = dayShopLocalTime;
      end = dayShopLocalTime;
    }

    // Get the OTA and the hotel
    const { store } = this;
    const ota = liveshopOta
      ? (store.peekRecord<Ota>('ota', liveshopOta) as Ota)
      : (store.peekRecord<Ota>('ota', this.selectedOTA as string) as Ota);
    const hotel = store.peekRecord<Hotel>(
      'hotel',
      this.currentHotelId as string,
    ) as Hotel;

    // Create the liveupdate object
    const liveupdateObject: LiveupdateObject = {
      hotel,
      type: LIVEUPDATE_TYPE_RATES,
      ota,
      fromDate: start,
      toDate: end,
      sendEmail: email,
      customRange: custom,
      // Hardcode this to make sure the liveupdates pass the checks in selectionObserver before we have received the POST response
      priority: 0,
      params: {
        compset_ids: [parseInt(this.compsetId as unknown as string, 10)],
        los: this.los,
        mealtype: this.mealtype,
        membershiptype: this.membershipType,
        persons: this.maxPersons,
        platform: this.platform,
        roomtype: this.roomtype,
      },
      labels: isShopReduction ? 'shop_reduction' : '',
    };

    mapRateParamToAPIParams(this.rateParam, liveupdateObject.params);

    const { compareType, compareValue } = this;
    if (isComparingRates(compareType, compareValue)) {
      liveupdateObject.type = LIVEUPDATE_TYPE_RATES_COMPARE;
      // By default we pass an empty object to compare_params
      liveupdateObject.params.compare_params = {};

      // Add the QP we are currently comparing on with the compareValue for compares that require an extra liveupdate
      if (this.isCompareWithExtraLiveupdate(compareType, compareValue)) {
        if (compareType === COMPARETYPES.RATE_PARAM) {
          liveupdateObject.params.compare_params['rate_type'] =
            this.compareValue === RATE_PARAMS.THIRD_PARTY
              ? RATETYPES.THIRD_PARTY
              : RATETYPES.NONE;
        } else {
          // For COMPARETYPES.LOS, COMPARETYPES.PERSONS, COMPARETYPES.PLATFORM and COMPARETYPES.MEMBERSHIPTYPE
          // los, persons, platform and membershipType should all be casted to integers!
          liveupdateObject.params.compare_params[compareType] = parseInt(
            this.compareValue,
            10,
          );
        }
      }
    }

    // Check if the liveupdate params are correct
    const message = this.checkLiveUpdateRates(liveupdateObject);

    this.startLiveupdate(liveupdateObject, message, custom, isShopReduction);
  }

  private startLiveupdate(
    liveupdateObject: Partial<RawLiveupdate>,
    message: string,
    custom = false,
    isShopReduction = false,
  ): void {
    // If there is no message all is correct
    if (isEmpty(message)) {
      const liveupdate = this.createLiveupdate(liveupdateObject);

      try {
        liveupdate.save().then(
          (liveupdatePayload) => {
            // We delete the temporary record in store
            this.removeLiveupdate(liveupdate);

            this.handleLiveupdatePayload(
              liveupdatePayload as {
                liveupdates: [RawLiveupdate, ...RawLiveupdate[]];
              },
              custom,
            );

            // Trigger fetching new liveupdate limits
            this.debounceFetchLiveupdates();
          },
          (response) => {
            this.handleLiveupdateErrorResponse(
              liveupdate,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              response.payload,
              isShopReduction,
            );
          },
        );
      } catch (error) {
        if (isShopReduction) {
          this.errorTracking.addBreadcrumb({
            message: 'Shop reduction liveupdate error',
            data: { error },
          });
        }
        this.handleLiveupdateCreateError(error, liveupdate);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.displayGenericLiveupdateError(
        this.intl.t(message).toString(),
        isShopReduction,
      );
    }
  }

  private startLiveupdatePolling(interval?: number): void {
    debounce(
      this,
      () => {
        if (macroCondition(isTesting())) {
          return;
        }

        if (this.liveupdateTimer !== null) {
          return;
        }

        set(
          this,
          'liveupdateTimer',
          this.scheduleLiveupdatePollEvent(
            // eslint-disable-next-line @typescript-eslint/unbound-method
            this.onLiveupdatePollEvent,
            interval,
          ),
        );
      },
      500,
    );
  }

  private async stopPolling(): Promise<void> {
    await this.startPollingTask.cancelAll();
    await this.schedulePollEventTask.cancelAll();
  }

  updateSelection(selections: Partial<LiveupdateSelections>): void {
    // Merge defaults with the new selections
    const defaultSelections: LiveupdateSelections & {
      showMemberRate: boolean;
    } = {
      // Id of the hotel
      currentHotelId: null,
      // Id of the selected OTA
      selectedOTA: null,
      los: 1,
      month: null,
      mealtype: null,
      maxPersons: 0,
      roomtype: null,
      rateParam: RATE_PARAMS.LOWEST,
      membershipType: 0,
      platform: PLATFORMS.PLATFORM_UNSPECIFIED,
      rateChanges: '',
      compsetId: 1,
      meta: 'nested',
      isParityAnalytics: false,
      showMemberRate: false,
    };
    setProperties(defaultSelections, selections);
    setProperties(this, defaultSelections);
  }
}

declare module '@ember/service' {
  interface Registry {
    liveupdate: LiveupdateService;
  }
}
